import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconArrowsMaximize, IconArrowsMinimize, IconX } from "@tabler/icons-react";

type ExpandableDetailDialogProps = {
  opened: boolean;
  title: string;
  titleId: string;
  closeAriaLabel: string;
  resetKey: string | number | null;
  onClose: () => void;
  children: ReactNode;
};

export function ExpandableDetailDialog({
  opened,
  title,
  titleId,
  closeAriaLabel,
  resetKey,
  onClose,
  children,
}: ExpandableDetailDialogProps) {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!opened) setExpanded(true);
  }, [opened]);

  useEffect(() => {
    setExpanded(true);
  }, [resetKey]);

  useEffect(() => {
    if (!opened) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [opened, onClose]);

  if (!opened || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] ${
        expanded
          ? "flex h-[100dvh] w-screen flex-col bg-[#FAFAFA]"
          : "flex items-center justify-center bg-slate-900/40 p-4"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={expanded ? undefined : onClose}
    >
      <div
        className={
          expanded
            ? "flex min-h-0 flex-1 flex-col"
            : "flex max-h-[92vh] w-[min(1100px,96vw)] flex-col overflow-hidden rounded-xl bg-[#FAFAFA] shadow-xl"
        }
        onClick={expanded ? undefined : (event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[#E5E7EB] bg-white px-4 py-3">
          <h2
            id={titleId}
            className="min-w-0 truncate text-sm font-semibold text-[#111827]"
          >
            {title}
          </h2>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              aria-label={expanded ? "Restore to popup" : "Open full screen"}
              className="rounded-lg border border-[#E5E7EB] bg-white p-2 text-[#6B7280] hover:bg-[#FAFAFA]"
            >
              {expanded ? (
                <IconArrowsMinimize size={18} />
              ) : (
                <IconArrowsMaximize size={18} />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeAriaLabel}
              className="rounded-lg border border-[#E5E7EB] bg-white p-2 text-[#6B7280] hover:bg-[#FAFAFA]"
            >
              <IconX size={18} />
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <div className={expanded ? "mx-auto w-full max-w-6xl" : "w-full"}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
